import { Avatar, formatNumber, useTranslation } from '@hbs-us/ui';

import type { OverviewRecentTime } from '../overviewTypes.js';

import { HomeCard } from './HomeCard.js';

/**
 * Recent time entries card (prod DashboardPage recent-time block): the last six
 * entries with the server-computed hours + labor cost. The SPA NEVER recomputes
 * the lunch/OT math — it renders the server values verbatim (D-03). Token-styled.
 *
 * Fed from the single `/overview` response the route already holds, so it has no pending or error
 * state of its own: by the time this renders, that request has answered. Only `empty` is real here.
 */

export function RecentTime({
  entries,
  money,
  formatDate,
}: {
  entries: OverviewRecentTime[];
  money: (value: number) => string;
  formatDate: (date: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return (
    <HomeCard
      headingId="home-recent-time-heading"
      title={t('home:recentTime.heading')}
      status={entries.length === 0 ? 'empty' : 'ready'}
      emptyText={t('home:recentTime.empty')}
    >
      <ul className="mt-3 grid divide-y divide-border">
        {entries.map((entry) => (
          <li
            key={`${entry.date}-${entry.employeeName}-${entry.jobName ?? ''}-${entry.hours}-${entry.laborCost}`}
            className="flex items-center justify-between gap-3 py-2.5 text-sm first:pt-0 last:pb-0"
          >
            <span className="flex min-w-0 items-center gap-3">
              <Avatar name={entry.employeeName} size="sm" />
              <span className="min-w-0">
                <span className="block truncate font-medium text-foreground">
                  {entry.employeeName}
                </span>
                <span className="block truncate text-muted-foreground text-xs">
                  {entry.jobName
                    ? t('home:recentTime.jobDate', {
                        jobName: entry.jobName,
                        date: formatDate(entry.date),
                      })
                    : formatDate(entry.date)}
                </span>
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block font-semibold tabular-nums text-foreground">
                {t('home:recentTime.hours', {
                  hours: formatNumber(entry.hours || 0, locale, { maximumFractionDigits: 2 }),
                })}
              </span>
              <span className="block text-muted-foreground text-xs tabular-nums">
                {/* null: the server hid wages from this reader; a dash, never $0. */}
                {(entry.laborCost as number | null) === null ? '—' : money(entry.laborCost)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}
